/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  Box,
  Checkbox,
  FormControlLabel,
  makeStyles,
  Theme,
} from '@material-ui/core';
import {
  ContextPanelContentSection,
  ContextPanelDataDisplay,
  FormikValmetTextInput,
  GhostButton,
  ValmetTextArea,
  colors,
  IOption,
  LoadingScreen,
} from '@valmet-iop/ui-common';
import {
  ReservationInputDTO,
  ReservationOutputDTO,
  ReservationType,
} from 'api/generated/iop';
import {DatePickerInput} from 'components/Common/DatePickerInput';
import {Actions} from 'components/Common/Entity/Actions';
import {useItem} from 'components/Common/Entity/context/item-context';
import {useRedirect} from 'components/Common/Entity/context/redirect-context';
import {ItemContentLayout} from 'components/Common/Entity/ItemLayout';
import {TextInput} from 'components/Common/TextInput';
import {usePrevious} from 'utils/hooks';
import {
  useLogisticsOperatorTrucksGet,
  usePostReservations,
  usePostReservationsSearch,
  usePutReservationsReservationId,
  usePutReservationsReservationRestrictedId,
  useReservationAvailableProducts,
  useReservationAvailableProductsForAllProviders,
  useReservationsLogisticCompaniesCustomerIdGet,
  useSiteParametersGet,
  useCompanyParametersBySiteGet,
} from 'components/Hooks/queries/useReservations';
import {Form, Formik, useField, useFormikContext} from 'formik';
import * as React from 'react';

import {UseMutationResult} from 'react-query';
import {
  getIntervalText,
  useItemDataEdit,
  useReservationType,
} from '../../hooks/useItemData';
import {generateEventQs, QsEvents} from '../../routes';
import {useTranslation} from 'react-i18next';
import {
  getReservationSchema,
  ReservationFormType,
  validationMaintenanceInPastSchema,
  validationMaintenanceSchema,
  getReservationSchemaOnPastRestricted,
} from './validationSchema';
import {addMinutes, isFuture, isPast, isSameDay} from 'date-fns';
import {AxiosResponse} from 'axios';
import {ContentMaintenance, ContentReservation} from '../ItemRead';
import {ConfirmDialog, DialogDefaultYesNoButtons} from '../ConfirmDialog';
import {
  PERMISSION_ALIAS_SCHEDULING_MAINTENANCE,
  PERMISSION_ALIAS_SCHEDULING_RESERVATIONS,
  usePermission,
} from 'context/permission-context';
import {orderOptionsBy} from 'utils/form';
import {
  isMaintenanceUpdatable,
  isReservationUpdatable,
  isReservationUpdatableAll,
  isReservationUpdatableRestricted,
} from '../..';
import {
  DEFAULT_RESERVATION_LENGTH_MINUTES,
  ensureAboveMinDate,
  getNearestPossibleStartTime,
} from '../../timeUtils';
import AutoSelectingFormikSingleSelect, {
  IAutoSelect,
} from 'components/Common/AutoSelectingFormikSingleSelect';
import {uniq, zipObj} from 'ramda';
import {useForm} from '../../context/form-context';
import {
  getBooleanValueFromParams,
  KnownCompanyParameterIds,
  KnownSiteParameterIds,
  SiteParameters,
} from 'utils/parameters';
import {isReservationTypeContent} from '.';
import {usePageView} from '../../context/pageView-context';

export const FormikValmetTextArea = (props: any) => {
  const {formProperty, ...rest} = props;
  const {touched, errors, values, setFieldValue, setFieldTouched} =
    useFormikContext<any>();
  const fieldTouched = touched[formProperty] as boolean | undefined;
  const fieldError = errors[formProperty] as string | undefined;
  const fieldValue = values[formProperty] as string;

  const onChange = (val: string) => {
    setFieldValue(formProperty, val);
  };
  const onBlur = () => {
    setFieldTouched(formProperty);
  };

  return (
    <ValmetTextArea
      {...rest}
      value={fieldValue}
      errorMessageTranslationKey={fieldTouched ? fieldError : undefined}
      onChange={onChange}
      onBlur={onBlur}
      width="full"
    />
  );
};
interface IMultiLineLayout {
  left: React.ReactNode;
  right: React.ReactNode;
  labelOffset?: boolean;
}
function MultiLineLayout({left, right, labelOffset}: IMultiLineLayout) {
  // MarginTop for right Box is the offset of the
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      marginBottom="16px"
    >
      <Box>{left}</Box>
      <Box marginTop={labelOffset && '20px'}>{right}</Box>
    </Box>
  );
}
function OverlapObserver({
  children,
  mutationPost,
  mutationPut,
}: {
  children: React.ReactNode;
  mutationPost: UseMutationResult<
    AxiosResponse<ReservationOutputDTO>,
    unknown,
    ReservationInputDTO,
    unknown
  >;
  mutationPut: UseMutationResult<
    AxiosResponse<ReservationOutputDTO>,
    unknown,
    {
      reservationId: string;
    } & ReservationInputDTO,
    unknown
  >;
}) {
  const {isCreate} = useItem();
  const {t} = useTranslation();
  const {values} = useFormikContext<any>();
  const {
    options: {reservation},
    normalizeValuesToPostPut,
  } = useItemDataEdit();
  const [fieldIsOverlapOnMutate, , helpersFieldIsOverlapOnMutate] = useField({
    name: 'isOverlapOnMutate',
  });

  return (
    <>
      {children}
      <ConfirmDialog
        isOpen={fieldIsOverlapOnMutate.value}
        text="scheduling:notifications.intervalExists"
        onResult={isConfirmed => {
          if (isConfirmed) {
            if (isCreate) {
              mutationPost.mutate(normalizeValuesToPostPut(values));
            } else {
              mutationPut.mutate({
                ...normalizeValuesToPostPut(values),
                reservationId: reservation?.reservationId ?? '',
              });
            }
          }
          helpersFieldIsOverlapOnMutate.setValue(false);
        }}
        buttons={({onClose, onConfirm}) => {
          return [
            {
              text: t('forms.confirmDialog.no'),
              type: 'default',
              onClick: onClose,
            },
            {
              text: t('forms.confirmDialog.yes'),
              type: 'ghost',
              onClick: onConfirm,
            },
          ];
        }}
      />
    </>
  );
}
interface IStep {
  title: (isLoading?: boolean) => React.ReactElement;
  setConfirmEvent: (ev: (() => void) | null) => void;
}
export function MaintenanceStep({title, setConfirmEvent}: IStep): JSX.Element {
  const formContext = useForm();
  const {isCreate, isUpdate} = useItem();
  const {toDefault} = useRedirect();
  const {
    initialValues,
    options: {reservation},
    normalizeValuesToPostPut,
    normalizeValuesToPostSearchOverlapping,
    getDefaultReservationTimeIntervalMinutes,
  } = useItemDataEdit();
  const {mutationPost, mutationPut} = usePostPutMutations();

  const mutationSearchPost = usePostReservationsSearch();

  const [siteId, setSiteId] = React.useState<string>('');
  const [defaultLoadingTime, setDefaultLoadingTime] = React.useState<
    number | undefined
  >(undefined);

  const isAnyMutationLoading =
    mutationPost.isLoading ||
    mutationPut.isLoading ||
    mutationSearchPost.isLoading;

  const isDisabled = !isMaintenanceUpdatable(initialValues.updatePermissions);
  const validationSchema =
    !isUpdate || !isPast(new Date(initialValues.loadingTimeStart))
      ? validationMaintenanceSchema
      : validationMaintenanceInPastSchema;

  const start = new Date(initialValues.loadingTimeStart);
  const end = new Date(initialValues.loadingTimeEnd);
  const isEndDateAutoUpdated = !isUpdate || isFuture(new Date(start));

  const onChangeSite = (selectedSiteId: string) => {
    setSiteId(selectedSiteId);
    setDefaultLoadingTime(
      getDefaultReservationTimeIntervalMinutes(
        selectedSiteId,
        undefined,
        ReservationType.Maintenance,
      ) || undefined,
    );
  };

  const onChangeLoadingPlace = (selectedEquipmentId: string) =>
    setDefaultLoadingTime(
      getDefaultReservationTimeIntervalMinutes(
        siteId,
        selectedEquipmentId,
        ReservationType.Maintenance,
      ) || undefined,
    );

  return (
    <>
      <Formik
        initialValues={{
          ...initialValues,
          type: {
            text: ReservationType.Maintenance,
            value: ReservationType.Maintenance,
          },
        }}
        onSubmit={async (values, actions) => {
          try {
            const {data} = await mutationSearchPost.mutateAsync(
              normalizeValuesToPostSearchOverlapping(values),
            );
            if (
              data.resultCount &&
              data.data?.some(item => isReservationTypeContent(item.type))
            ) {
              actions.setFieldValue('isOverlapOnMutate', true);
            } else {
              if (isCreate) {
                await mutationPost.mutateAsync({
                  ...normalizeValuesToPostPut(values),
                });
              } else {
                await mutationPut.mutateAsync({
                  ...normalizeValuesToPostPut(values),
                  reservationId: reservation?.reservationId ?? '',
                });
              }
            }
            formContext.setFormManuallyReset(false);
          } finally {
            actions.setSubmitting(false);
          }
        }}
        validationSchema={validationSchema}
      >
        <Form>
          {title(isAnyMutationLoading)}
          <OverlapObserver
            mutationPost={mutationPost}
            mutationPut={mutationPut}
          >
            <ContentLayout
              header="scheduling:headers.maintenanceInfo"
              isLoading={isAnyMutationLoading}
              actions={
                isDisabled ? (
                  <></>
                ) : (
                  <Actions
                    disabled={isAnyMutationLoading}
                    onDirtyCancel={() => {
                      setConfirmEvent(() => () => {
                        toDefault();
                        formContext.setFormManuallyReset(false);
                      });
                    }}
                  />
                )
              }
            >
              {isDisabled ? (
                <ContentMaintenance />
              ) : (
                <>
                  <SiteField
                    spyOnChange={onChangeSite}
                    autoUpdateEndDate={isEndDateAutoUpdated}
                  />
                  <LoadingPlaceField
                    spyOnChange={onChangeLoadingPlace}
                    autoUpdateEndDate={isEndDateAutoUpdated}
                  />
                  <TextInput
                    formProperty="displayName"
                    id="displayName"
                    labelTranslationKey="scheduling:fields.title"
                    isRequired
                    width="full"
                  />

                  <MultiLineLayout
                    labelOffset
                    left={
                      <TimeStartField
                        isUpdate={isUpdate}
                        startDate={start}
                        endDate={end}
                        defaultLoadingTime={defaultLoadingTime}
                        autoUpdateEndDate={isEndDateAutoUpdated}
                      />
                    }
                    right={
                      <TimeEndField
                        isUpdate={isUpdate}
                        startDate={start}
                        endDate={end}
                      />
                    }
                  />

                  <Interval />
                  <FormikValmetTextArea
                    rows={5}
                    formProperty="comments"
                    id="comments"
                    labelTranslationKey="scheduling:fields.comments"
                  />
                </>
              )}
            </ContentLayout>
          </OverlapObserver>
        </Form>
      </Formik>
    </>
  );
}
const useStylesInterval = makeStyles<Theme, {isUpdate: boolean}>(() => ({
  root: props => {
    return {
      '& p': {
        color: props.isUpdate ? colors.black : colors.grey3,
      },
    };
  },
}));
function Interval() {
  const [fieldLoadingTimeStart] = useField({
    name: 'loadingTimeStart',
  });
  const [fieldLoadingTimeEnd] = useField({
    name: 'loadingTimeEnd',
  });
  const {isUpdate} = useItem();
  const classes = useStylesInterval({isUpdate});
  return (
    <Box className={classes.root} mb={2}>
      <ContextPanelDataDisplay
        labelTranslationKey={'scheduling:fields.timeInterval'}
        value={getIntervalText({
          start: new Date(fieldLoadingTimeStart.value),
          end: new Date(fieldLoadingTimeEnd.value),
        })}
      />
    </Box>
  );
}

interface ISelectedStartDateHelper {
  startDate: Date;
  updatedStartDate: Date;
}

interface ITimeField {
  noDateLimit?: boolean;
  isUpdate: boolean;
  startDate?: Date;
  endDate?: Date;
  defaultLoadingTime?: number;
  autoUpdateEndDate?: boolean;
}

function TimeStartField({
  noDateLimit,
  isUpdate = false,
  startDate,
  endDate,
  defaultLoadingTime,
  autoUpdateEndDate,
}: ITimeField) {
  const {setFieldValue} = useFormikContext();
  const [fieldLoadingTimeStart] = useField({
    name: 'loadingTimeStart',
  });
  const [fieldType] = useField({
    name: 'type',
  });
  const [fieldIsCompleted] = useField({
    name: 'isCompleted',
  });

  // In case of a "Confirmed reservation"
  // Allows to rewind start date changes without having to reset whole form
  const [askConfirm, setAskConfirm] = React.useState(false);
  const toggleAskConfirmModal = () => setAskConfirm(!askConfirm);
  const [startTimeUpdateHelper, setStartTimeUpdateHelper] = React.useState({
    startDate: new Date(),
    updatedStartDate: new Date(),
  } as ISelectedStartDateHelper);

  const {value: start} = fieldLoadingTimeStart;

  // If updating a PAST item, limit date selection
  const isDateSelectionLimited = isUpdate && startDate && isPast(startDate);
  const minDate = isDateSelectionLimited
    ? startDate
    : getNearestPossibleStartTime(new Date());
  const maxDate = isDateSelectionLimited ? endDate : undefined;

  // If minDate === new Date, limit time selection
  const minTime = isSameDay(new Date(minDate), new Date(start))
    ? minDate
    : undefined;

  // If maxDate === new Date, limit time selection
  const maxTime =
    maxDate && isSameDay(new Date(maxDate), new Date(start))
      ? maxDate
      : undefined;

  // Ensures we stay above minimum date
  const doStartDateChange = (newStartDate: Date) => {
    const validNewStartDate = ensureAboveMinDate(
      newStartDate,
      noDateLimit ? undefined : minDate,
    );
    setFieldValue('loadingTimeStart', validNewStartDate);

    // Update endDate based on the site.defaultLoadingTime if on CREATION or UPDATING FUTURE item
    if (autoUpdateEndDate)
      setFieldValue(
        'loadingTimeEnd',
        addMinutes(
          validNewStartDate,
          defaultLoadingTime || DEFAULT_RESERVATION_LENGTH_MINUTES,
        ),
      );
  };
  return (
    <>
      <DatePickerInput
        formProperty="loadingTimeStart"
        labelTranslationKey={
          fieldType.value === ReservationType.Maintenance
            ? 'scheduling:fields.maintenanceTime'
            : 'scheduling:fields.reservationTime'
        }
        onChange={updatedStart => {
          if (!updatedStart) return; // To please TS
          if (fieldIsCompleted.value && isFuture(updatedStart)) {
            // If reservation is already completed, asks to confirm the moving to future
            setStartTimeUpdateHelper({
              startDate: start,
              updatedStartDate: updatedStart,
            } as ISelectedStartDateHelper);
            toggleAskConfirmModal();
          } else {
            doStartDateChange(updatedStart);
          }
        }}
        width="inline"
        isRequired
        noMinDateLimit={noDateLimit}
        noMaxDateLimit={noDateLimit}
        minDate={minDate}
        maxDate={maxDate}
        minTime={minTime}
        maxTime={maxTime}
      />
      <ConfirmDialog
        isOpen={askConfirm}
        text="scheduling:notifications.confirmCompleteFutureReservation"
        onResult={isConfirmed => {
          if (isConfirmed) {
            doStartDateChange(startTimeUpdateHelper.updatedStartDate);
          } else {
            // Revert date change to original value
            setFieldValue('loadingTimeStart', startTimeUpdateHelper.startDate);
          }
          toggleAskConfirmModal();
        }}
        buttons={DialogDefaultYesNoButtons}
      />
    </>
  );
}
function TimeEndField({
  noDateLimit,
  isUpdate = false,
  startDate,
  endDate,
  isReservation,
}: ITimeField & {isReservation?: boolean}) {
  const {setFieldTouched} = useFormikContext();
  const [fieldLoadingTimeStart] = useField({
    name: 'loadingTimeStart',
  });
  const [fieldLoadingTimeEnd] = useField({
    name: 'loadingTimeEnd',
  });

  const {value: start} = fieldLoadingTimeStart;
  const {value: end} = fieldLoadingTimeEnd;

  // TODO: add function for defaultMininumIntervalTime here
  const minDate = start;
  const maxDate =
    isUpdate && startDate && isPast(startDate) ? endDate : undefined;

  // In update mode, if item.startDate === new endDate, limit time selection
  // In both mode, it is possible to create an item of length === 0min
  const minTime = isSameDay(new Date(minDate), new Date(end))
    ? minDate
    : undefined;

  // In update mode, if item.endDate === new endDate, limit time selection
  const maxTime =
    maxDate && isSameDay(new Date(maxDate), new Date(end))
      ? endDate
      : undefined;

  React.useEffect(() => {
    setFieldTouched('loadingTimeEnd');
  }, [setFieldTouched, start, end]);

  return (
    <DatePickerInput
      formProperty="loadingTimeEnd"
      width="inline"
      noMinDateLimit={!isReservation && noDateLimit}
      noMaxDateLimit={noDateLimit}
      minDate={minDate}
      maxDate={maxDate}
      minTime={minTime}
      maxTime={maxTime}
    />
  );
}

export function usePostPutMutations() {
  const mutationPost = usePostReservations();
  const mutationPut = usePutReservationsReservationId();
  return {mutationPost, mutationPut};
}

export function useRestrictedPutMutations() {
  const mutationPutRestricted = usePutReservationsReservationRestrictedId();
  return {mutationPutRestricted};
}
export function ReservationStep({title, setConfirmEvent}: IStep): JSX.Element {
  const formContext = useForm();
  const {isCreate, isUpdate} = useItem();
  const {toDefault} = useRedirect();
  const {
    initialValues,
    options: {reservation},
    normalizeValuesToPostPut,
    normalizeValuesToPutRestricted,
    getDefaultReservationTimeIntervalMinutes,
  } = useItemDataEdit();
  const {t} = useTranslation();
  const {mutationPost, mutationPut} = usePostPutMutations();
  const {mutationPutRestricted} = useRestrictedPutMutations();
  const {isUpdatableCompleted, isUpdatablePastAll} = usePermission();
  const isAnyMutationLoading =
    mutationPost.isLoading ||
    mutationPut.isLoading ||
    mutationPutRestricted.isLoading;

  const [siteId, setSiteId] = React.useState<string>('');
  const [reservationType, setReservationType] = React.useState<
    ReservationType | undefined
  >(undefined);
  const [defaultLoadingTime, setDefaultLoadingTime] = React.useState<
    number | undefined
  >(undefined);
  const {data: siteParameters} = useSiteParametersGet(siteId);

  const truckAssetFieldEnabled =
    siteParameters !== undefined &&
    getBooleanValueFromParams(
      siteParameters,
      KnownSiteParameterIds.TruckAssetFieldEnabled,
      false,
    );
  const orderNoFieldGenerated =
    siteParameters !== undefined &&
    getBooleanValueFromParams(
      siteParameters,
      KnownSiteParameterIds.OrderNoFieldGenerated,
      false,
    );
  const {data: companyParameters} = useCompanyParametersBySiteGet(siteId);
  const referenceOrderNumberFieldEnabled =
    companyParameters !== undefined &&
    getBooleanValueFromParams(
      companyParameters,
      KnownCompanyParameterIds.ReferenceOrderNumberFieldEnabled,
      false,
    );

  const isUpdateRestrictedFields = isReservationUpdatableRestricted(
    initialValues.updatePermissions,
  );
  const isUpdatablePastAllPermission = isUpdatablePastAll(
    PERMISSION_ALIAS_SCHEDULING_RESERVATIONS,
  );
  const noDateLimit =
    isUpdate && (isUpdatablePastAllPermission || isUpdateRestrictedFields);
  const isDisabled = !isReservationUpdatable(initialValues.updatePermissions);
  const isUpdateAllFields = isReservationUpdatableAll(
    initialValues.updatePermissions,
  );

  const validationReservationSchema = React.useMemo(() => {
    return noDateLimit && isUpdateAllFields
      ? getReservationSchema({
          type: ReservationFormType.FullUpdate,
          truckAssetFieldEnabled,
          referenceOrderNumberFieldEnabled,
          orderNoFieldGenerated,
        })
      : isUpdateRestrictedFields
      ? getReservationSchemaOnPastRestricted()
      : getReservationSchema({
          type: ReservationFormType.Create,
          truckAssetFieldEnabled,
          referenceOrderNumberFieldEnabled,
          orderNoFieldGenerated,
        });
  }, [
    noDateLimit,
    isUpdateAllFields,
    truckAssetFieldEnabled,
    referenceOrderNumberFieldEnabled,
    orderNoFieldGenerated,
    isUpdateRestrictedFields,
  ]);

  const start = new Date(initialValues.loadingTimeStart);
  const end = new Date(initialValues.loadingTimeEnd);
  // autoUpdateEndDate is always true for Reservations

  const onChangeSite = (selectedSiteId: string) => {
    setSiteId(selectedSiteId);
    setDefaultLoadingTime(
      getDefaultReservationTimeIntervalMinutes(
        selectedSiteId,
        undefined,
        reservationType,
      ) || undefined,
    );
  };

  const onChangeLoadingPlace = (selectedEquipmentId: string) => {
    setDefaultLoadingTime(
      getDefaultReservationTimeIntervalMinutes(
        siteId,
        selectedEquipmentId,
        reservationType,
      ) || undefined,
    );
  };

  const onChangeReservationType = (reservationType: string) => {
    var type: ReservationType =
      ReservationType[reservationType as keyof typeof ReservationType];
    setReservationType(type);
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationReservationSchema}
        onSubmit={async (values, actions) => {
          const {amount} = validationReservationSchema.cast(
            values,
          ) as typeof values;
          // Don't send Order number if it is auto-generated
          const orderNo = orderNoFieldGenerated ? '' : values.orderNo;
          const castValues = {...values, amount, orderNo};
          const isCompleted = isUpdatableCompleted(
            PERMISSION_ALIAS_SCHEDULING_RESERVATIONS,
          )
            ? values.isCompleted
            : null;
          try {
            if (isCreate) {
              await mutationPost.mutateAsync({
                ...normalizeValuesToPostPut(castValues),
                isCompleted,
              });
            } else {
              if (isUpdateRestrictedFields) {
                await mutationPutRestricted.mutateAsync({
                  ...normalizeValuesToPutRestricted(castValues),
                  isCompleted,
                  reservationId: reservation?.reservationId ?? '',
                });
              } else {
                await mutationPut.mutateAsync({
                  ...normalizeValuesToPostPut(castValues),
                  isCompleted,
                  reservationId: reservation?.reservationId ?? '',
                });
              }
            }
            actions.resetForm();
            formContext.setFormManuallyReset(false);
          } catch (e) {
            console.error(e);
          } finally {
            actions.setSubmitting(false);
          }
        }}
      >
        <Form>
          {title()}
          <ContentLayout
            header="scheduling:headers.reservationInfo"
            isLoading={isAnyMutationLoading}
            actions={
              isDisabled ? (
                <></>
              ) : (
                <Actions
                  disabled={isAnyMutationLoading}
                  onDirtyCancel={() => {
                    setConfirmEvent(() => () => {
                      toDefault();
                      formContext.setFormManuallyReset(false);
                    });
                  }}
                />
              )
            }
          >
            {isUpdateRestrictedFields ? (
              <ContentReservation
                surrogates={[
                  {
                    label: 'scheduling:fields.destination',
                    value: <DestinationField />,
                  },
                  {
                    label: 'scheduling:fields.reservationTimeStart',
                    value: (
                      <MultiLineLayout
                        labelOffset
                        left={
                          <TimeStartField
                            noDateLimit
                            defaultLoadingTime={defaultLoadingTime}
                            isUpdate
                            autoUpdateEndDate
                          />
                        }
                        right={
                          <TimeEndField noDateLimit isUpdate isReservation />
                        }
                      />
                    ),
                  },
                  {
                    label: 'scheduling:fields.reservationTimeEnd',
                    value: <Interval />,
                  },
                  {
                    label: 'scheduling:fields.completed',
                    value: <CheckboxCompleted isReservation />,
                  },
                ]}
              />
            ) : isDisabled ? (
              <ContentReservation />
            ) : (
              <>
                <SiteField spyOnChange={onChangeSite} autoUpdateEndDate />
                <ProductField />
                <ReservationTypeField
                  spyOnChange={onChangeReservationType}
                  autoUpdateEndDate
                />
                <LoadingPlaceField
                  spyOnChange={onChangeLoadingPlace}
                  autoUpdateEndDate
                />
                <CustomerField />
                <LogisticsOperatorField />
                {truckAssetFieldEnabled && <TruckAssetField />}
                <MultiLineLayout
                  labelOffset
                  left={
                    <TimeStartField
                      noDateLimit={noDateLimit}
                      isUpdate={isUpdate}
                      startDate={start}
                      endDate={end}
                      defaultLoadingTime={defaultLoadingTime}
                      autoUpdateEndDate
                    />
                  }
                  right={
                    <TimeEndField
                      noDateLimit={noDateLimit}
                      isUpdate={isUpdate}
                      startDate={start}
                      endDate={end}
                      isReservation
                    />
                  }
                />
                <Interval />
                <OrderNoField
                  initialOrderNo={initialValues.orderNo}
                  siteParameters={siteParameters}
                  orderNoFieldGenerated={orderNoFieldGenerated}
                />

                {referenceOrderNumberFieldEnabled && (
                  <FormikValmetTextInput
                    formProperty="referenceOrderNumber"
                    id="referenceOrderNumber"
                    labelTranslationKey="scheduling:fields.referenceOrderNumber"
                    width="full"
                  />
                )}

                <DestinationField />

                <FormikValmetTextInput
                  formProperty="amount"
                  id="amount"
                  labelTranslationKey="scheduling:fields.amount"
                  isRequired
                  width="full"
                  unit={t('units.kg')}
                />
                <FormikValmetTextArea
                  rows={5}
                  formProperty="comments"
                  id="comments"
                  labelTranslationKey="scheduling:fields.comments"
                />
                <CheckboxCompleted isReservation />
              </>
            )}
          </ContentLayout>
        </Form>
      </Formik>
    </>
  );
}
function OrderNoField({
  siteParameters,
  orderNoFieldGenerated,
  initialOrderNo,
}: {
  siteParameters: SiteParameters | undefined;
  orderNoFieldGenerated: boolean;
  initialOrderNo: string;
}) {
  const [, , helper] = useField({name: 'orderNo'});
  const [isDisabled, setDisabled] = React.useState(true);
  React.useEffect(() => {
    if (isDisabled && siteParameters !== undefined && !orderNoFieldGenerated) {
      // Field currently disabled, site parameters loaded, order no is manually input
      // Enable input
      setDisabled(false);
    } else if (
      !isDisabled &&
      siteParameters !== undefined &&
      orderNoFieldGenerated
    ) {
      // Field currently enabled, site parameters loaded, order no is auto-generated
      // Disable input
      setDisabled(true);
      // Reset field to initial value
      // In create case this will be empty (since it will be auto-generated)
      // In edit case it will be the one in DB as it cannot be changed when site has auto-generation
      helper.setValue(initialOrderNo);
    }
  }, [
    isDisabled,
    siteParameters,
    orderNoFieldGenerated,
    helper,
    initialOrderNo,
  ]);

  return (
    <FormikValmetTextInput
      formProperty="orderNo"
      id="orderNo"
      labelTranslationKey="scheduling:fields.orderNo"
      isRequired={!orderNoFieldGenerated}
      isDisabled={isDisabled}
      width="full"
    />
  );
}
function ContentLayout({
  children,
  actions,
  header,
  isLoading,
}: {
  children: React.ReactNode;
  actions: React.ReactNode;
  header: string;
  isLoading: boolean;
}) {
  return (
    <>
      <ItemContentLayout>
        {isLoading ? (
          <LoadingScreen />
        ) : (
          <ContextPanelContentSection headerTranslationKey={header}>
            {children}
          </ContextPanelContentSection>
        )}
      </ItemContentLayout>
      <>{actions}</>
    </>
  );
}
function LogisticsOperatorField() {
  const {formManuallyReset} = useForm();
  const [fieldSite] = useField({name: 'site'});
  const [fieldLoadingPlace] = useField({name: 'loadingPlace'});
  const [fieldCustomer] = useField({name: 'customer'});
  const [fieldProduct, , helperProduct] = useField({name: 'product'});
  const [fieldLogisticOperator, , helperLogisticOperator] = useField({
    name: 'logisticOperator',
  });
  const [fieldTruckAsset, , helperTruckAsset] = useField({
    name: 'truckAsset',
  });

  const {
    options: {
      filterLogisticsOperators,
      getProviderCompanyIdBySiteId,
      getFilteredProducts,
    },
    initialValues,
  } = useItemDataEdit();

  const customerId = fieldCustomer.value?.value;

  const {refetch, data} =
    useReservationsLogisticCompaniesCustomerIdGet(customerId);
  React.useEffect(() => {
    if (customerId) {
      void refetch();
    }
  }, [customerId, refetch]);
  const selectedSiteId =
    fieldSite?.value?.value || initialValues.site?.value || '';
  const selectedProductId = fieldProduct?.value?.value;
  const selectedLogisticsOperatorId = fieldLogisticOperator?.value?.value;
  const selectedLoadingPlaceId = fieldLoadingPlace?.value?.value;
  const selectedTruckAssetId = fieldTruckAsset?.value?.value;
  const {data: availableProducts, isFetched: availableProductsFetched} =
    useReservationAvailableProducts(
      getProviderCompanyIdBySiteId(selectedSiteId),
    );
  return (
    <AutoSelectingFormikSingleSelectWithInitialOptionsObserver
      disableAutoSelect={!availableProductsFetched}
      isDisabled={!fieldCustomer.value?.value}
      formProperty="logisticOperator"
      id="logisticOperator"
      labelTranslationKey="scheduling:fields.logisticsOperator"
      isRequired
      options={filterLogisticsOperators(
        orderOptionsBy(
          data?.map(({companyId, displayName}) => {
            return {
              text: displayName ?? '',
              value: companyId ?? '',
            };
          }) || [],
        ),
        availableProducts,
        {productId: selectedProductId},
      )}
      spyOnChange={selected => {
        if (selected?.value) {
          helperLogisticOperator.setTouched(false);
        }
        if (
          !Array.isArray(selected) &&
          (selected?.value !== selectedLogisticsOperatorId || !selected?.value)
        ) {
          helperTruckAsset.setValue(null);
        }

        if (
          !Array.isArray(selected) &&
          selected !== null &&
          selected.value !== selectedLogisticsOperatorId
        ) {
          if (!formManuallyReset) {
            setDefaultProductIfNeeded(
              selected.value,
              selectedProductId,
              availableProducts?.defaultProductIdsByLogisticsOperatorCompanyId,
              getFilteredProducts.bind(null, availableProducts, {
                siteId: selectedSiteId,
                loadingPlaceId: selectedLoadingPlaceId,
                customerId,
                logisticsOperatorId: selected.value,
                truckAssetId: selectedTruckAssetId,
              }),
              helperProduct.setValue,
            );
          }
        }
      }}
    />
  );
}

function TruckAssetField() {
  const [fieldSite] = useField({name: 'site'});
  const [fieldLogisticOperator] = useField({
    name: 'logisticOperator',
  });
  const [fieldProduct] = useField({name: 'product'});

  const {
    options: {getProviderCompanyIdBySiteId},
    initialValues,
  } = useItemDataEdit();

  const selectedSiteId =
    fieldSite?.value?.value || initialValues.site?.value || '';
  const providerCompanyId = getProviderCompanyIdBySiteId(selectedSiteId) ?? '';
  const selectedLogisticsOperatorId = fieldLogisticOperator?.value?.value;
  const selectedProductId = fieldProduct?.value?.value;

  const {refetch, data} = useLogisticsOperatorTrucksGet(
    providerCompanyId,
    selectedLogisticsOperatorId,
  );
  const {data: availableProducts, isFetched: availableProductsFetched} =
    useReservationAvailableProducts(providerCompanyId);

  React.useEffect(() => {
    if (providerCompanyId && selectedLogisticsOperatorId) {
      void refetch();
    }
  }, [providerCompanyId, selectedLogisticsOperatorId, refetch]);
  const availableProductsProducts = availableProducts?.products;
  const options = React.useMemo(() => {
    return (
      data
        ?.filter(x => {
          if (!selectedProductId) {
            // No product selected, list assets that allow _any_ product
            return (
              availableProductsProducts?.some(
                p => p.allowedAssetIds?.includes(x.truckId) ?? false,
              ) ?? false
            );
          } else {
            // Product selected, list assets that allow this product
            const allowedAssetIds =
              availableProductsProducts?.find(
                p => p.productId === selectedProductId,
              )?.allowedAssetIds ?? [];
            return allowedAssetIds.includes(x.truckId);
          }
        })
        .map(x => ({text: x.truckLicensePlate, value: x.truckId})) ?? []
    );
  }, [availableProductsProducts, data, selectedProductId]);

  return (
    <AutoSelectingFormikSingleSelectWithInitialOptionsObserver
      disableAutoSelect={!availableProductsFetched}
      isDisabled={!fieldLogisticOperator.value?.value || data === undefined}
      formProperty="truckAsset"
      id="truckAsset"
      labelTranslationKey="scheduling:fields.truckAsset"
      isRequired
      options={options}
    />
  );
}

function setDefaultProductIfNeeded(
  selectedCompanyId: string | null | undefined,
  selectedProductId: string | null | undefined,
  defaultProductIdsByCompanyId: Record<string, string> | null | undefined,
  getFilteredProductOptions: () => IOption[],
  setProductOption: (value: any) => void,
) {
  if (!selectedCompanyId) {
    return;
  }

  // If product already selected, do nothing
  if (!!selectedProductId) {
    return;
  }

  // Sanity check for TS
  if (!defaultProductIdsByCompanyId) {
    return;
  }

  // Check if this company has a default product
  const defaultProductId = defaultProductIdsByCompanyId[selectedCompanyId];
  if (!defaultProductId) {
    return;
  }

  // Check if that product is available once this company is chosen
  const updatedOptions = getFilteredProductOptions();
  const defaultProductIndex = updatedOptions.findIndex(
    o => o.value === defaultProductId,
  );
  if (defaultProductIndex < 0) {
    return;
  }

  // Set the default product as selected
  setProductOption(updatedOptions[defaultProductIndex]);
}

function SiteField({
  spyOnChange,
  autoUpdateEndDate,
}: {
  spyOnChange?: (siteId: string) => void;
  autoUpdateEndDate?: boolean;
}) {
  const formContext = useForm();
  const [fieldSite] = useField({name: 'site'});
  const [fieldProduct, , helperProduct] = useField({name: 'product'});

  const [fieldType] = useField({name: 'type'});
  const selectedType = fieldType?.value?.value || undefined;

  const [, , helperLoadingPlace] = useField({name: 'loadingPlace'});
  const [, , helperCustomer] = useField({
    name: 'customer',
  });
  const [, , helperLogisticOperator] = useField({
    name: 'logisticOperator',
  });
  const [, , helperDestination] = useField({
    name: 'destination',
  });
  const [, , helperTruckAsset] = useField({
    name: 'truckAsset',
  });
  const {
    initialValues,
    options: {
      site: siteOptions,
      getFilteredSites,
      getProviderCompanyIdBySiteId,
    },
    getDefaultReservationTimeIntervalMinutes,
  } = useItemDataEdit();

  const {setFieldValue} = useFormikContext();
  const [fieldLoadingTimeStart] = useField({
    name: 'loadingTimeStart',
  });
  const {value: start} = fieldLoadingTimeStart;

  const text =
    fieldSite?.value?.text ||
    (!formContext.formManuallyReset && initialValues.site?.text) ||
    '';
  const value =
    fieldSite?.value?.value ||
    (!formContext.formManuallyReset && initialValues.site?.value) ||
    '';

  React.useEffect(() => {
    if (spyOnChange) {
      spyOnChange(value);
    }
  }, [spyOnChange, value]);
  const selectedProductId = fieldProduct?.value?.value;
  const {isCreate} = useItem();
  const {isSelectedPageViewList, isSelectedPageViewTimeline} = usePageView();
  const providerCompanyIds = uniq(
    siteOptions
      .map(s => getProviderCompanyIdBySiteId(s.value))
      .filter(x => x !== undefined) as string[],
  );
  const availableProductsResults =
    useReservationAvailableProductsForAllProviders(providerCompanyIds);
  const availableProductsDtosByProviderCompanyId = zipObj(
    providerCompanyIds,
    availableProductsResults.map(x => x.data),
  );
  const isUpdatableField =
    isMaintenanceUpdatable(initialValues.updatePermissions) ||
    isReservationUpdatableAll(initialValues.updatePermissions);

  if (
    (isSelectedPageViewList || isSelectedPageViewTimeline) &&
    (isCreate || isUpdatableField)
  ) {
    return (
      <AutoSelectingFormikSingleSelectWithInitialOptionsObserver
        formProperty="site"
        disableAutoSelect={availableProductsResults.some(r => !r.isFetched)}
        id="site"
        labelTranslationKey="scheduling:headers.site"
        isRequired
        options={getFilteredSites(availableProductsDtosByProviderCompanyId, {
          productId: selectedProductId,
        })}
        spyOnChange={selected => {
          if (autoUpdateEndDate) {
            const defaultLoadingTime = getDefaultReservationTimeIntervalMinutes(
              selected?.value,
              undefined,
              selectedType,
            );

            setFieldValue(
              'loadingTimeEnd',
              addMinutes(
                start,
                defaultLoadingTime || DEFAULT_RESERVATION_LENGTH_MINUTES,
              ),
            );
          }

          if (!Array.isArray(selected) && selected?.value !== value) {
            helperLoadingPlace.setValue(null);
            helperCustomer.setValue(null);
            helperLogisticOperator.setValue(null);
            helperProduct.setValue(null);
            helperDestination.setValue(null);
            helperTruckAsset.setValue(null);
          }
        }}
      />
    );
  } else {
    return (
      <ContextPanelDataDisplay
        labelTranslationKey="scheduling:headers.site"
        value={text}
      />
    );
  }
}

function ReservationTypeField({
  spyOnChange,
  autoUpdateEndDate,
}: {
  spyOnChange?: (reservationType: string) => void;
  autoUpdateEndDate?: boolean;
}) {
  const formContext = useForm();
  const [fieldSite] = useField({name: 'site'});
  const [fieldProduct] = useField({name: 'product'});
  const [fieldLoadingPlace] = useField({name: 'loadingPlace'});
  const [fieldCustomer] = useField({name: 'customer'});
  const [fieldLogisticOperator] = useField({
    name: 'logisticOperator',
  });
  const [fieldType] = useField({name: 'type'});
  const [fieldTruckAsset] = useField({name: 'truckAsset'});
  const {isDisabled} = useReservationType();
  const {
    initialValues,
    options: {
      getProviderCompanyIdBySiteId,
      getReservationTypes,
      getFilteredProducts,
    },
    getDefaultReservationTimeIntervalMinutes,
  } = useItemDataEdit();

  const selectedSiteId =
    fieldSite?.value?.value ||
    (!!fieldType.value && initialValues.site?.value) ||
    '';
  const {data: availableProducts, isFetched: availableProductsLoaded} =
    useReservationAvailableProducts(
      getProviderCompanyIdBySiteId(selectedSiteId),
    );
  const selectedLoadingPlaceId = fieldLoadingPlace.value?.value || '';
  const selectedCustomerId = fieldCustomer.value?.value ?? '';
  const selectedLogisticsOperatorId = fieldLogisticOperator?.value?.value ?? '';
  const productValue = fieldProduct.value?.value || '';
  const selectedTruckAssetId = fieldTruckAsset?.value?.value;

  const productOptions = getFilteredProducts(availableProducts, {
    siteId: selectedSiteId,
    loadingPlaceId: selectedLoadingPlaceId,
    customerId: selectedCustomerId,
    logisticsOperatorId: selectedLogisticsOperatorId,
    truckAssetId: selectedTruckAssetId,
  });
  const optionIds = productOptions.map(({value}) => value);
  const productsByFilteredOptions = availableProducts?.products?.filter(
    ({productId}) => {
      return optionIds.includes(productId || '');
    },
  );

  const options = selectedSiteId
    ? getReservationTypes(
        productsByFilteredOptions,
        productValue,
        selectedLoadingPlaceId,
      )
    : [];

  const {setFieldValue} = useFormikContext();
  const [fieldLoadingTimeStart] = useField({
    name: 'loadingTimeStart',
  });
  const {value: start} = fieldLoadingTimeStart;

  const value =
    fieldType?.value?.value ||
    (!formContext.formManuallyReset && initialValues.type?.value) ||
    '';

  React.useEffect(() => {
    if (spyOnChange) {
      spyOnChange(value);
    }
  }, [spyOnChange, value]);

  return (
    <AutoSelectingFormikSingleSelectWithInitialOptionsObserver
      isDisabled={
        !selectedSiteId ||
        !availableProductsLoaded ||
        isDisabled(availableProducts)
      }
      formProperty="type"
      isRequired
      id="type"
      labelTranslationKey="scheduling:fields.type"
      options={options}
      spyOnChange={selected => {
        if (autoUpdateEndDate) {
          const newType = selected?.value
            ? ReservationType[selected.value as keyof typeof ReservationType]
            : undefined;

          const defaultLoadingTime = getDefaultReservationTimeIntervalMinutes(
            selectedSiteId,
            selectedLoadingPlaceId,
            newType,
          );

          setFieldValue(
            'loadingTimeEnd',
            addMinutes(
              start,
              defaultLoadingTime || DEFAULT_RESERVATION_LENGTH_MINUTES,
            ),
          );
        }
      }}
    />
  );
}

function LoadingPlaceField({
  spyOnChange,
  autoUpdateEndDate,
}: {
  spyOnChange?: (equipmentId: string) => void;
  autoUpdateEndDate?: boolean;
}) {
  const [fieldSite] = useField({name: 'site'});
  const [fieldProduct] = useField({name: 'product'});
  const [fieldType] = useField({name: 'type'});
  const [fieldLoadingPlace] = useField({name: 'loadingPlace'});

  const formContext = useForm();

  const {
    options: {getFilteredLoadingPlaces, getProviderCompanyIdBySiteId},
    initialValues,
    getDefaultReservationTimeIntervalMinutes,
  } = useItemDataEdit();
  const selectedSiteId =
    fieldSite?.value?.value ||
    (!!fieldLoadingPlace.value && initialValues.site?.value) ||
    '';
  const selectedProductId = fieldProduct?.value?.value;
  const selectedType = fieldType?.value?.value || undefined;

  const {data: availableProducts} = useReservationAvailableProducts(
    getProviderCompanyIdBySiteId(selectedSiteId),
  );
  const {setFieldValue} = useFormikContext();
  const [fieldLoadingTimeStart] = useField({
    name: 'loadingTimeStart',
  });
  const {value: start} = fieldLoadingTimeStart;

  const value =
    fieldLoadingPlace?.value?.value ||
    (!formContext.formManuallyReset && initialValues.loadingPlace?.value) ||
    '';

  React.useEffect(() => {
    if (spyOnChange) {
      spyOnChange(value);
    }
  }, [spyOnChange, value]);

  return (
    <AutoSelectingFormikSingleSelectWithInitialOptionsObserver
      isDisabled={!selectedSiteId}
      formProperty="loadingPlace"
      id="loadingPlace"
      labelTranslationKey="scheduling:fields.loadingPlace"
      isRequired
      options={
        selectedSiteId
          ? getFilteredLoadingPlaces(availableProducts, {
              siteId: selectedSiteId,
              productId: selectedProductId,
              type: selectedType,
            })
          : []
      }
      spyOnChange={selected => {
        if (autoUpdateEndDate) {
          const defaultLoadingTime = getDefaultReservationTimeIntervalMinutes(
            selectedSiteId,
            selected?.value,
            selectedType,
          );

          setFieldValue(
            'loadingTimeEnd',
            addMinutes(
              start,
              defaultLoadingTime || DEFAULT_RESERVATION_LENGTH_MINUTES,
            ),
          );
        }
      }}
    />
  );
}
function ProductField() {
  const [fieldSite] = useField({name: 'site'});
  const [fieldType] = useField({name: 'type'});
  const [fieldLoadingPlace] = useField({name: 'loadingPlace'});
  const [fieldCustomer] = useField({name: 'customer'});
  const [fieldProduct] = useField({name: 'product'});
  const [fieldLogisticOperator] = useField({
    name: 'logisticOperator',
  });
  const [fieldTruckAsset] = useField({name: 'truckAsset'});
  const {
    initialValues,
    options: {getProviderCompanyIdBySiteId, getFilteredProducts},
  } = useItemDataEdit();
  const selectedSiteId =
    fieldSite?.value?.value ||
    (!!fieldProduct.value && initialValues.site?.value) ||
    '';
  const selectedLoadingPlaceId = fieldLoadingPlace?.value?.value;
  const selectedCustomerId = fieldCustomer?.value?.value;
  const selectedLogisticsOperatorId = fieldLogisticOperator?.value?.value;
  const selectedTruckAssetId = fieldTruckAsset?.value?.value;
  const {data: availableProducts, isFetched: availableProductsLoaded} =
    useReservationAvailableProducts(
      getProviderCompanyIdBySiteId(selectedSiteId),
    );
  let options = selectedSiteId
    ? getFilteredProducts(availableProducts, {
        siteId: selectedSiteId,
        loadingPlaceId: selectedLoadingPlaceId,
        customerId: selectedCustomerId,
        logisticsOperatorId: selectedLogisticsOperatorId,
        truckAssetId: selectedTruckAssetId,
      })
    : [];
  const optionIds = options.map(({value}) => value);

  const {filterOptionsByMarkKey} = useReservationType();

  const productsByFilteredOptions = availableProducts?.products?.filter(
    ({productId}) => {
      return optionIds.includes(productId || '');
    },
  );

  const typeValue = fieldType.value?.value || '';

  if (!!typeValue) {
    options = filterOptionsByMarkKey(
      typeValue === ReservationType.ReservationLoading
        ? 'canProvide'
        : 'canReceive',
      options,
      selectedLoadingPlaceId,
      productsByFilteredOptions,
    );
  }

  return (
    <AutoSelectingFormikSingleSelectWithInitialOptionsObserver
      isDisabled={!selectedSiteId || !availableProductsLoaded}
      formProperty="product"
      id="product"
      labelTranslationKey="scheduling:fields.product"
      options={orderOptionsBy(options)}
      isRequired
    />
  );
}
function CustomerField() {
  const {formManuallyReset} = useForm();
  const [fieldSite] = useField({name: 'site'});
  const [fieldProduct, , helperProduct] = useField({name: 'product'});
  const [, , helpersDestination] = useField({
    name: 'destination',
  });
  const [fieldLogisticOperator, , helpersLogisticOperator] = useField({
    name: 'logisticOperator',
  });
  const [fieldCustomer] = useField({
    name: 'customer',
  });
  const [fieldLoadingPlace] = useField({name: 'loadingPlace'});
  const [fieldTruckAsset, , helperTruckAsset] = useField({
    name: 'truckAsset',
  });
  const {
    initialValues,
    options: {
      getFilteredCustomerCompanies,
      getProviderCompanyIdBySiteId,
      getFilteredProducts,
    },
  } = useItemDataEdit();
  const selectedSiteId =
    fieldSite?.value?.value ||
    (!!fieldCustomer.value && initialValues.site?.value) ||
    '';
  const selectedProductId = fieldProduct?.value?.value;
  const selectedLogisticsOperatorId = fieldLogisticOperator?.value?.value;
  const selectedLoadingPlaceId = fieldLoadingPlace?.value?.value;
  const selectedTruckAssetId = fieldTruckAsset?.value?.value;
  const {data: availableProducts, isFetched: availableProductsFetched} =
    useReservationAvailableProducts(
      getProviderCompanyIdBySiteId(selectedSiteId),
    );
  const [customerId, setCustomerId] = React.useState<string>('');
  const {refetch} = useReservationsLogisticCompaniesCustomerIdGet(customerId);
  React.useEffect(() => {
    if (customerId) {
      void refetch();
    }
  }, [customerId, refetch]);
  return (
    <AutoSelectingFormikSingleSelectWithInitialOptionsObserver
      disableAutoSelect={!availableProductsFetched}
      isDisabled={!selectedSiteId}
      formProperty="customer"
      id="customer"
      labelTranslationKey="scheduling:fields.customer"
      isRequired
      options={
        selectedSiteId
          ? getFilteredCustomerCompanies(availableProducts, {
              siteId: selectedSiteId,
              logisticsOperatorId: selectedLogisticsOperatorId,
              productId: selectedProductId,
            })
          : []
      }
      spyOnChange={selected => {
        if (
          !Array.isArray(selected) &&
          (selected?.value !== fieldCustomer.value?.value || !selected?.value)
        ) {
          helpersDestination.setValue(null);
          helpersLogisticOperator.setValue(null);
          helperTruckAsset.setValue(null);
          if (selected?.value) {
            setCustomerId(selected.value);
          }

          if (!formManuallyReset) {
            setDefaultProductIfNeeded(
              selected?.value,
              selectedProductId,
              availableProducts?.defaultProductIdsByCustomerCompanyId,
              getFilteredProducts.bind(null, availableProducts, {
                siteId: selectedSiteId,
                loadingPlaceId: selectedLoadingPlaceId,
                customerId: selected?.value ?? '',
                logisticsOperatorId: selectedLogisticsOperatorId,
                truckAssetId: selectedTruckAssetId,
              }),
              helperProduct.setValue,
            );
          }
        }
      }}
    />
  );
}
function DestinationField() {
  const [fieldCustomer] = useField({name: 'customer'});
  const {
    options: {getDestinationsByCustomerCompanyId},
  } = useItemDataEdit();

  const selectedCustomerId = fieldCustomer.value?.value;
  return (
    <AutoSelectingFormikSingleSelectWithInitialOptionsObserver
      isDisabled={!selectedCustomerId}
      formProperty="destination"
      id="destination"
      labelTranslationKey="scheduling:fields.destination"
      isRequired
      options={
        selectedCustomerId
          ? getDestinationsByCustomerCompanyId(selectedCustomerId)
          : []
      }
    />
  );
}

function CheckboxCompleted({
  isReservation,
}: {
  isReservation?: boolean;
}): JSX.Element {
  const {t} = useTranslation();
  const {isCreate} = useItem();
  const [{value}, , {setValue}] = useField({name: 'isCompleted'});
  const [fieldLoadingTimeStart, ,] = useField({name: 'loadingTimeStart'});
  const {isUpdatableCompleted} = usePermission();
  const [askConfirm, setAskConfirm] = React.useState(false);
  const isHidden =
    isCreate ||
    !isUpdatableCompleted(
      isReservation
        ? PERMISSION_ALIAS_SCHEDULING_RESERVATIONS
        : PERMISSION_ALIAS_SCHEDULING_MAINTENANCE,
    );
  const startTimeInFuture = isFuture(fieldLoadingTimeStart.value);
  if (isHidden) {
    return <></>;
  }
  return (
    <>
      <FormControlLabel
        control={
          <Checkbox
            checked={value}
            onChange={ev => {
              if (startTimeInFuture && ev.target.checked) {
                setAskConfirm(true);
              } else {
                setValue(ev.target.checked);
              }
            }}
            inputProps={{'aria-label': 'primary checkbox'}}
          />
        }
        label={t('scheduling:fields.completed')}
      />
      <ConfirmDialog
        isOpen={askConfirm}
        text="scheduling:notifications.confirmCompleteFutureReservation"
        onResult={isConfirmed => {
          if (isConfirmed) {
            setValue(true);
          }
          setAskConfirm(false);
        }}
        buttons={DialogDefaultYesNoButtons}
      />
    </>
  );
}

const useStyles = makeStyles<Theme>(theme => ({
  btnChoice: {
    width: '100%',
    marginBottom: theme.spacing(1),
    '&:hover': {
      transform: 'none',
    },
  },
}));

export function ChoiceStep(): JSX.Element {
  const {t} = useTranslation();
  const {toCreate} = useRedirect();
  const classes = useStyles();
  const {isCreatable} = usePermission();
  return (
    <ItemContentLayout>
      <GhostButton
        text={t('scheduling:buttons.reservation')}
        onClick={() => toCreate(generateEventQs(QsEvents.RESERVATION))}
        className={classes.btnChoice}
        isDisabled={!isCreatable(PERMISSION_ALIAS_SCHEDULING_RESERVATIONS)}
      />
      <GhostButton
        text={t('scheduling:buttons.maintenance')}
        onClick={() => toCreate(generateEventQs(QsEvents.MAINTENANCE))}
        className={classes.btnChoice}
        isDisabled={!isCreatable(PERMISSION_ALIAS_SCHEDULING_MAINTENANCE)}
      />
    </ItemContentLayout>
  );
}

function AutoSelectingFormikSingleSelectWithInitialOptionsObserver(
  props: IAutoSelect,
) {
  const prevOptions = usePrevious(props.options);
  const {isUpdate, isCreate} = useItem();
  const {addDropdownValueOnReset} = useForm();
  const {initialValues} = useItemDataEdit();
  const isUpdatableField =
    isMaintenanceUpdatable(initialValues.updatePermissions) ||
    isReservationUpdatableAll(initialValues.updatePermissions);
  const {isSelectedPageViewList, isSelectedPageViewTimeline} = usePageView();
  const isSiteFieldSelectable =
    (isSelectedPageViewList || isSelectedPageViewTimeline) &&
    (isCreate || isUpdatableField);
  React.useEffect(() => {
    // we need to store initial options on form mount
    if (
      isUpdate &&
      (prevOptions?.length === 0 || !prevOptions) &&
      props.options.length
    ) {
      addDropdownValueOnReset(
        props.formProperty,
        props.options,
        isSiteFieldSelectable,
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.options, isUpdate, props.formProperty, isSiteFieldSelectable]);

  return <AutoSelectingFormikSingleSelect {...props} />;
}
